import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatUtcForDisplay } from '../utils/dateTime';

const formatImovelLabel = (imovel) => {
  if (!imovel) return '';
  const numero = imovel.numero ? `Unid. ${imovel.numero}` : `ID ${imovel.id}`;
  const bloco = imovel.bloco ? ` - Bloco ${imovel.bloco}` : '';
  const desc = imovel.descricao ? ` - ${imovel.descricao}` : '';
  return `${numero}${bloco}${desc}`;
};

function EncomendaBaixaWizard({ open, onClose, onSuccess, initialEncomenda = null }) {
  const { user } = useAuth();

  const [imovelDialogOpen, setImovelDialogOpen] = useState(false);
  const [encomendaDialogOpen, setEncomendaDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [selectedImovel, setSelectedImovel] = useState(null);
  const [imovelOptions, setImovelOptions] = useState([]);
  const [loadingImoveis, setLoadingImoveis] = useState(false);

  const [encomendasAbertas, setEncomendasAbertas] = useState([]);
  const [loadingEncomendas, setLoadingEncomendas] = useState(false);
  const [selectedEncomendas, setSelectedEncomendas] = useState([]);

  const [pessoaRetirouOptions, setPessoaRetirouOptions] = useState([]);
  const [loadingPessoaRetirou, setLoadingPessoaRetirou] = useState(false);
  const [selectedPessoaRetirou, setSelectedPessoaRetirou] = useState(null);
  const [dataHoraRetirada, setDataHoraRetirada] = useState('');
  const [observacoesRetirada, setObservacoesRetirada] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const allSelected = encomendasAbertas.length > 0 && selectedEncomendas.length === encomendasAbertas.length;

  const debouncedFetchImoveis = useMemo(
    () => debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=10`)
        .then((response) => setImovelOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar imoveis:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  const debouncedFetchPessoasRetirou = useMemo(
    () => debounce((searchTerm) => {
      setLoadingPessoaRetirou(true);
      api.get(`/pessoas?search=${encodeURIComponent(searchTerm)}`)
        .then((response) => setPessoaRetirouOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas (retirada):', error))
        .finally(() => setLoadingPessoaRetirou(false));
    }, 500),
    []
  );

  const resetState = () => {
    setImovelDialogOpen(false);
    setEncomendaDialogOpen(false);
    setConfirmDialogOpen(false);
    setSelectedImovel(null);
    setImovelOptions([]);
    setEncomendasAbertas([]);
    setSelectedEncomendas([]);
    setPessoaRetirouOptions([]);
    setSelectedPessoaRetirou(null);
    setDataHoraRetirada('');
    setObservacoesRetirada('');
    setSubmitting(false);
  };

  useEffect(() => () => {
    debouncedFetchImoveis.cancel();
    debouncedFetchPessoasRetirou.cancel();
  }, [debouncedFetchImoveis, debouncedFetchPessoasRetirou]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (initialEncomenda) {
      setSelectedEncomendas([initialEncomenda]);
      setSelectedPessoaRetirou(
        initialEncomenda.destinatario_id
          ? { id: initialEncomenda.destinatario_id, nome: initialEncomenda.destinatario_nome || '' }
          : null
      );
      setConfirmDialogOpen(true);
      return;
    }

    setImovelDialogOpen(true);
  }, [open, initialEncomenda]);

  const closeAll = () => {
    resetState();
    onClose();
  };

  const fetchEncomendasAbertas = async (imovelId) => {
    setLoadingEncomendas(true);
    try {
      const resp = await api.get(`/encomendas/abertas-por-imovel/${imovelId}`);
      const list = resp.data?.data || [];
      setEncomendasAbertas(list);
      return list;
    } catch (error) {
      console.error('Erro ao buscar encomendas abertas:', error);
      alert('Erro ao buscar encomendas em aberto desta residência.');
      return [];
    } finally {
      setLoadingEncomendas(false);
    }
  };

  const handleAvancarImovel = async () => {
    if (!selectedImovel) return;
    const list = await fetchEncomendasAbertas(selectedImovel.id);
    setImovelDialogOpen(false);
    setEncomendaDialogOpen(true);

    if (!list.length) {
      alert('Nenhuma encomenda em aberto para esta residência.');
    }
  };

  const toggleEncomenda = (encomenda) => {
    setSelectedEncomendas((prev) => {
      const exists = prev.some((e) => e.id === encomenda.id);
      if (exists) {
        return prev.filter((e) => e.id !== encomenda.id);
      }
      return [...prev, encomenda];
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedEncomendas([]);
    } else {
      setSelectedEncomendas([...encomendasAbertas]);
    }
  };

  const handleAvancarEncomendas = () => {
    if (!selectedEncomendas.length) return;

    const firstDestinatario = selectedEncomendas[0].destinatario_id;
    const allSameDestinatario = selectedEncomendas.every((e) => e.destinatario_id === firstDestinatario);

    if (allSameDestinatario && firstDestinatario) {
      setSelectedPessoaRetirou(
        { id: firstDestinatario, nome: selectedEncomendas[0].destinatario_nome || '' }
      );
    }

    setEncomendaDialogOpen(false);
    setConfirmDialogOpen(true);
  };

  const handleConfirmarBaixa = async () => {
    if (!selectedEncomendas.length) return;
    if (!selectedPessoaRetirou?.id) {
      alert('Selecione a pessoa que retirou.');
      return;
    }
    if (!dataHoraRetirada) {
      alert('Informe a data e hora da retirada.');
      return;
    }

    const isBatch = selectedEncomendas.length > 1;

    setSubmitting(true);
    try {
      if (isBatch) {
        const resp = await api.post('/encomendas/baixar-lote', {
          encomenda_ids: selectedEncomendas.map((e) => e.id),
          pessoa_retirou_id: selectedPessoaRetirou.id,
          data_hora_retirada: dataHoraRetirada || null,
          observacoes: observacoesRetirada || null,
        });

        const data = resp.data;
        let msg = `${data.sucesso} de ${data.total} encomenda(s) baixada(s) com sucesso.`;
        if (data.ja_retiradas > 0) {
          msg += `\n${data.ja_retiradas} já havia(m) sido retirada(s).`;
        }
        if (data.nao_encontradas > 0) {
          msg += `\n${data.nao_encontradas} não encontrada(s).`;
        }

        const wppSent = (data.whatsapp || []).filter((w) => w.sent).length;
        const wppTotal = (data.whatsapp || []).length;
        if (wppTotal > 0) {
          msg += `\nWhatsApp: ${wppSent}/${wppTotal} notificação(ões) enviada(s).`;
        }

        alert(msg);
      } else {
        const resp = await api.post(`/encomendas/${selectedEncomendas[0].id}/baixar`, {
          pessoa_retirou_id: selectedPessoaRetirou.id,
          data_hora_retirada: dataHoraRetirada || null,
          observacoes: observacoesRetirada || null,
        });

        const whatsapp = resp.data?.whatsapp;
        const msg = resp.data?.message || 'Retirada registrada com sucesso.';
        if (whatsapp?.sent) {
          alert(`${msg}\nWhatsApp enviado ao destinatário.`);
        } else if (whatsapp && whatsapp.sent === false) {
          alert(`${msg}\nWhatsApp não enviado: ${whatsapp.reason || 'motivo não informado'}.`);
        } else {
          alert(msg);
        }
      }

      onSuccess();
      closeAll();
    } catch (error) {
      const msg = error.response?.data?.message || 'Erro ao registrar retirada.';
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={imovelDialogOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>Baixar encomenda - escolher residência</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            options={imovelOptions}
            value={selectedImovel}
            onChange={(event, newValue) => setSelectedImovel(newValue)}
            getOptionLabel={(option) => formatImovelLabel(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                if (newInputValue) debouncedFetchImoveis(newInputValue);
                else setImovelOptions([]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Residência (imóvel)"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Dica: digite o número da unidade, bloco ou descrição.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancelar</Button>
          <Button variant="contained" onClick={handleAvancarImovel} disabled={!selectedImovel}>
            Avançar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={encomendaDialogOpen} onClose={closeAll} maxWidth="md" fullWidth>
        <DialogTitle>
          Baixar encomenda - escolher encomenda
          {selectedEncomendas.length > 0 && (
            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'primary.main' }}>
              ({selectedEncomendas.length} selecionada{selectedEncomendas.length > 1 ? 's' : ''})
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
            {selectedImovel ? formatImovelLabel(selectedImovel) : ''}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          {loadingEncomendas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Button
                variant="text"
                size="small"
                onClick={toggleSelectAll}
                sx={{ mb: 1 }}
              >
                {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
              </Button>
              <List dense>
                {encomendasAbertas.map((e) => (
                  <ListItemButton key={e.id} onClick={() => toggleEncomenda(e)}>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedEncomendas.some((s) => s.id === e.id)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={`#${e.id} - ${e.codigo_rastreio || 'Sem rastreio'} - ${e.destinatario_nome || 'Destinatário N/A'}`}
                      secondary={`Recebida em: ${formatUtcForDisplay(e.data_hora_recebimento, user)}`}
                    />
                  </ListItemButton>
                ))}
                {!encomendasAbertas.length && (
                  <Box sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Nenhuma encomenda em aberto para esta residência.
                    </Typography>
                  </Box>
                )}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEncomendaDialogOpen(false);
              setSelectedEncomendas([]);
              setImovelDialogOpen(true);
            }}
          >
            Voltar
          </Button>
          <Button onClick={closeAll}>Fechar</Button>
          <Button
            variant="contained"
            onClick={handleAvancarEncomendas}
            disabled={!selectedEncomendas.length}
          >
            Avançar ({selectedEncomendas.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialogOpen} onClose={closeAll} maxWidth="sm" fullWidth>
        <DialogTitle>
          Confirmar retirada
          {selectedEncomendas.length > 1 && (
            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
              ({selectedEncomendas.length} encomendas)
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 1 }}>
            {selectedEncomendas.map((encomenda) => (
              <Typography key={encomenda.id} variant="body2" sx={{ color: 'text.secondary' }}>
                #{encomenda.id} — {encomenda.codigo_rastreio || 'Sem rastreio'} — {encomenda.destinatario_nome || 'Destinatário N/A'}
              </Typography>
            ))}
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Funcionário: {user?.email || user?.id || 'N/A'}
            </Typography>
          </Box>

          <Autocomplete
            fullWidth
            options={pessoaRetirouOptions}
            value={selectedPessoaRetirou}
            onChange={(event, newValue) => setSelectedPessoaRetirou(newValue)}
            getOptionLabel={(option) => option?.nome || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(event, newInputValue, reason) => {
              if (reason === 'input') {
                if (newInputValue) debouncedFetchPessoasRetirou(newInputValue);
                else setPessoaRetirouOptions([]);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Pessoa que retirou"
                margin="normal"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPessoaRetirou ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            label="Data e hora da retirada"
            type="datetime-local"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={dataHoraRetirada}
            onChange={(e) => setDataHoraRetirada(e.target.value)}
            helperText={`Deixe em branco para usar o horário atual do servidor. Se informar manualmente, será interpretado no fuso da empresa (${user?.empresa_timezone || 'America/Sao_Paulo'}).`}
          />

          <TextField
            label="Observações (opcional)"
            fullWidth
            margin="normal"
            multiline
            minRows={3}
            value={observacoesRetirada}
            onChange={(e) => setObservacoesRetirada(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAll}>Cancelar</Button>
          <Button variant="contained" onClick={handleConfirmarBaixa} disabled={submitting}>
            Confirmar retirada
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default EncomendaBaixaWizard;
